// Cliente del API de divflow-server. Toda escritura lleva la cabecera anti-CSRF.

export interface User {
  id: string;
  email: string;
  name: string;
  role: 'owner' | 'member';
  totpEnabled: boolean;
  createdAt: number;
  lastLoginAt: number | null;
}

export interface Domain {
  id: string;
  hostname: string;
  status: 'pending' | 'active';
  primary: boolean;
  verifyToken: string;
  createdAt: number;
  verifiedAt: number | null;
  lastCheckAt: number | null;
  lastError: string | null;
}

export interface Redirect {
  from: string;
  to: string;
  status: 301 | 302 | 307 | 308;
}

export interface HeaderRule {
  path: string;
  name: string;
  value: string;
}

export interface Site {
  id: string;
  slug: string;
  name: string;
  archived: boolean;
  createdAt: number;
  updatedAt: number;
  hosts: { base: string; preview: string; primary: string; url: string; previewUrl: string };
  published: { id: string; number: number; at: number } | null;
  passwordProtected: boolean;
  settings: { redirects: Redirect[]; headers: HeaderRule[] };
  domains: Domain[];
  uptime: { state: 'up' | 'down' | 'unknown'; lastCheckAt: number | null; lastOk: boolean | null };
}

export interface Release {
  id: string;
  number: number;
  createdAt: number;
  createdBy: string | null;
  fileCount: number;
  sizeBytes: number;
  note: string | null;
  source: string;
  live: boolean;
  staging: boolean;
}

export interface PublicConfig {
  baseDomain: string;
  appHost: string;
  serverIps: string[];
  publicScheme: string;
  publicPort: string;
  maxUploadMb: number;
}

export interface DomainCheck {
  ok: boolean;
  dnsOk: boolean;
  httpOk: boolean;
  records: { a: string[]; aaaa: string[]; cname: string[] };
  error: string | null;
  warning: string | null;
}

export interface BackupJob {
  at: number | null;
  ok: boolean | null;
  detail?: string;
}

export interface Status {
  sites: { id: string; name: string; state: 'up' | 'down' | 'unknown'; uptime24h: number | null; last: { at: number; ok: boolean; status: number | null; ms: number | null } | null }[];
  backups: { overall: 'ok' | 'late' | 'error' | 'unknown'; litestream: BackupJob; restic: BackupJob & { snapshot?: string; sizeBytes?: number }; check: BackupJob; drill: BackupJob };
  monitorMinutes: number;
}

export interface AuditEntry {
  id: number;
  at: number;
  user: string | null;
  action: string;
  target: string | null;
  detail: unknown;
}

export class ApiError extends Error {
  status: number;
  code: string;
  constructor(status: number, code: string, message?: string) {
    super(message ?? code);
    this.status = status;
    this.code = code;
  }
}

async function call<T>(method: string, url: string, body?: unknown, raw?: { data: Blob; type: string }): Promise<T> {
  const headers: Record<string, string> = {};
  if (method !== 'GET') headers['x-divflow-request'] = '1';
  let payload: BodyInit | undefined;
  if (raw) {
    headers['content-type'] = raw.type;
    payload = raw.data;
  } else if (body !== undefined) {
    headers['content-type'] = 'application/json';
    payload = JSON.stringify(body);
  }
  const res = await fetch(url, { method, headers, body: payload, credentials: 'same-origin' });
  const text = await res.text();
  const data = text ? (JSON.parse(text) as Record<string, unknown>) : {};
  if (!res.ok) throw new ApiError(res.status, String(data.error ?? 'error'), typeof data.message === 'string' ? data.message : undefined);
  return data as T;
}

export const api = {
  config: () => call<PublicConfig>('GET', '/api/config'),
  me: () => call<{ user: User }>('GET', '/api/me'),
  login: (email: string, password: string) => call<{ user?: User; mfa?: boolean; ticket?: string }>('POST', '/api/auth/login', { email, password }),
  mfa: (ticket: string, code: string) => call<{ user: User }>('POST', '/api/auth/mfa', { ticket, code }),
  logout: () => call<{ ok: true }>('POST', '/api/auth/logout'),
  changePassword: (current: string, next: string) => call<{ ok: true }>('POST', '/api/me/password', { current, next }),
  mfaSetup: () => call<{ secret: string; otpauthUrl: string; qrSvg: string }>('POST', '/api/me/2fa/setup'),
  mfaEnable: (code: string) => call<{ recoveryCodes: string[] }>('POST', '/api/me/2fa/enable', { code }),
  mfaDisable: (password: string, code: string) => call<{ ok: true }>('POST', '/api/me/2fa/disable', { password, code }),

  users: () => call<{ users: User[] }>('GET', '/api/users'),
  addUser: (u: { email: string; name: string; password: string; role: 'owner' | 'member' }) => call<{ user: User }>('POST', '/api/users', u),
  deleteUser: (id: string) => call<{ ok: true }>('DELETE', `/api/users/${id}`),

  sites: () => call<{ sites: Site[] }>('GET', '/api/sites'),
  site: (id: string) => call<{ site: Site }>('GET', `/api/sites/${id}`),
  createSite: (name: string, slug?: string) => call<{ site: Site }>('POST', '/api/sites', slug ? { name, slug } : { name }),
  updateSite: (id: string, patch: Partial<{ name: string; slug: string; archived: boolean; settings: Site['settings'] }>) =>
    call<{ site: Site }>('PATCH', `/api/sites/${id}`, patch),
  setPassword: (id: string, password: string | null) => call<{ site: Site }>('PUT', `/api/sites/${id}/password`, { password }),
  deleteSite: (id: string, confirm: string) => call<{ ok: true }>('DELETE', `/api/sites/${id}`, { confirm }),
  unpublish: (id: string) => call<{ site: Site }>('POST', `/api/sites/${id}/unpublish`),

  addDomain: (siteId: string, hostname: string) => call<{ domain: Domain }>('POST', `/api/sites/${siteId}/domains`, { hostname }),
  verifyDomain: (id: string) => call<{ domain: Domain; check: DomainCheck }>('POST', `/api/domains/${id}/verify`),
  primaryDomain: (id: string) => call<{ domain: Domain }>('POST', `/api/domains/${id}/primary`),
  removeDomain: (id: string) => call<{ ok: true }>('DELETE', `/api/domains/${id}`),

  releases: (siteId: string) => call<{ releases: Release[] }>('GET', `/api/sites/${siteId}/releases`),
  upload: (siteId: string, file: File, publish: boolean) =>
    call<{ release: Release; site: Site }>('PUT', `/api/sites/${siteId}/releases${publish ? '?publish=1' : ''}`, undefined, { data: file, type: 'application/zip' }),
  publishRelease: (id: string) => call<{ site: Site }>('POST', `/api/releases/${id}/publish`),
  stageRelease: (id: string) => call<{ site: Site }>('POST', `/api/releases/${id}/stage`),
  deleteRelease: (id: string) => call<{ ok: true }>('DELETE', `/api/releases/${id}`),

  forms: (siteId: string) => call<{ submissions: { id: string; createdAt: number; form: string | null; data: Record<string, string> }[] }>('GET', `/api/sites/${siteId}/forms`),
  status: () => call<Status>('GET', '/api/status'),
  audit: () => call<{ entries: AuditEntry[] }>('GET', '/api/audit'),
};
